import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RecommendedItem } from '../config/recommendedConfig';

interface BannerCarouselProps {
  items: RecommendedItem[];
  /** Título de la sección */
  title: string;
  /** Emoji decorativo opcional */
  emoji?: string;
  /** Intervalo de rotación en milisegundos (default: 4000) */
  interval?: number;
  /** Variante de color: 'primary' | 'purple' */
  variant?: 'primary' | 'purple';
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({
  items,
  title,
  emoji,
  interval = 4000,
  variant = 'primary',
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // No renderizar si no hay items
  if (items.length === 0) return null;

  const goToNext = useCallback(() => {
    if (items.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
      setIsTransitioning(false);
    }, 300);
  }, [items.length]);

  const goToPrev = useCallback(() => {
    if (items.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
      setIsTransitioning(false);
    }, 300);
  }, [items.length]);

  // Auto-rotación
  useEffect(() => {
    if (items.length <= 1 || isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(goToNext, interval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [goToNext, interval, isPaused, items.length]);

  const currentItem = items[currentIndex];

  const handleClick = () => {
    window.open(currentItem.url, '_blank', 'noopener,noreferrer');
  };

  // Colores según variante
  const colors = variant === 'purple'
    ? {
        titleColor: 'text-purple-700 dark:text-purple-300',
        bg: 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30',
        border: 'border-purple-200 dark:border-purple-800/50',
        dotActive: 'bg-purple-500',
        dotInactive: 'bg-purple-200 dark:bg-purple-700',
        hoverRing: 'hover:ring-purple-300 dark:hover:ring-purple-600',
        arrowBg: 'bg-purple-100/80 hover:bg-purple-200 dark:bg-purple-900/50 dark:hover:bg-purple-800/70',
        arrowText: 'text-purple-600 dark:text-purple-300',
        nameColor: 'text-purple-800 dark:text-purple-200',
        descColor: 'text-purple-600/80 dark:text-purple-400',
        label: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
      }
    : {
        titleColor: 'text-primary-700 dark:text-primary-300',
        bg: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30',
        border: 'border-blue-200 dark:border-blue-800/50',
        dotActive: 'bg-primary-500',
        dotInactive: 'bg-primary-200 dark:bg-primary-700',
        hoverRing: 'hover:ring-primary-300 dark:hover:ring-primary-600',
        arrowBg: 'bg-primary-100/80 hover:bg-primary-200 dark:bg-primary-900/50 dark:hover:bg-primary-800/70',
        arrowText: 'text-primary-600 dark:text-primary-300',
        nameColor: 'text-primary-800 dark:text-primary-200',
        descColor: 'text-primary-600/80 dark:text-primary-400',
        label: 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300',
      };

  return (
    <div className="w-full">
      {/* Título de sección */}
      <div className="flex items-center gap-1.5 mb-2">
        {emoji && <span className="text-sm">{emoji}</span>}
        <h3 className={`text-xs font-semibold uppercase tracking-wider ${colors.titleColor}`}>
          {title}
        </h3>
      </div>

      {/* Banner */}
      <div
        className={`relative rounded-xl border ${colors.border} ${colors.bg} overflow-hidden cursor-pointer
          transition-all duration-200 ring-0 ring-transparent ${colors.hoverRing} hover:ring-2 hover:shadow-md`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onClick={handleClick}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
        aria-label={`${currentItem.name}${currentItem.descriptionKey ? ' - ' + t(currentItem.descriptionKey) : ''}`}
      >
        <div className="flex items-center gap-3 p-3">
          {/* Imagen */}
          <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
            <img
              src={currentItem.image}
              alt={currentItem.name}
              className={`w-full h-full object-cover transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/base.svg';
              }}
            />
          </div>

          {/* Texto */}
          <div className={`flex-1 min-w-0 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <p className={`text-sm font-bold truncate ${colors.nameColor}`}>
              {currentItem.name}
            </p>
            {currentItem.descriptionKey && (
              <p className={`text-xs truncate ${colors.descColor}`}>
                {t(currentItem.descriptionKey)}
              </p>
            )}
          </div>

          {/* Flecha de enlace externo */}
          <div className="flex-shrink-0">
            <svg className={`w-4 h-4 ${colors.descColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </div>

        {/* Controles de navegación (solo si hay más de 1 item) */}
        {items.length > 1 && (
          <>
            {/* Flechas */}
            <button
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              className={`absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full ${colors.arrowBg} ${colors.arrowText}
                flex items-center justify-center opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity
                focus:opacity-100 focus:outline-none`}
              style={{ opacity: isPaused ? 1 : 0 }}
              aria-label="Anterior"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className={`absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full ${colors.arrowBg} ${colors.arrowText}
                flex items-center justify-center opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity
                focus:opacity-100 focus:outline-none`}
              style={{ opacity: isPaused ? 1 : 0 }}
              aria-label="Siguiente"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Indicadores de posición (dots) */}
            <div className="flex justify-center gap-1 pb-2">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (idx !== currentIndex) {
                      setIsTransitioning(true);
                      setTimeout(() => {
                        setCurrentIndex(idx);
                        setIsTransitioning(false);
                      }, 300);
                    }
                  }}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentIndex
                      ? `${colors.dotActive} w-3`
                      : colors.dotInactive
                  }`}
                  aria-label={`Ir a ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BannerCarousel;
